import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { getServitutDownloadFileName } from "src/managers/ServitutDownloadNaming";
import { fetchTingbogPdfBlob } from "src/managers/TingbogDownload";
import { buildLLMMatrikelData } from "src/modules/ANNModule/ANNLLMMatrikelData";
import { fetchServitutProcessor } from "src/utils/servitutProcessorServiceHost";
import { getServitutAktSearchTargets } from "./servitutAktSearch";

export {
   getServitutAktFetchCandidates,
   getServitutAktSearchTargets,
   getServitutPdfFetchAktId,
   isServitutFromKorrektAktSource,
   isServitutFromOvrigAktSource,
   isServitutFromTextAktSource,
   listServitutAktSearchLocations,
} from "./servitutAktSearch";

export interface LLMAnalyzerCallerInfo {
   userRef: string;
   userName: string;
   organizationId: string;
}

export interface LLMMetadata {
   id?: string;
   document: string;
   documentUrl?: string | null;
   aktId: string | null;
   fromOvrigDataSource?: boolean | null;
   caseDescription?: string | null;
   matrikelData?: {
      ejerlav: string;
      matnr: string;
      BFEnummer: string;
      adresse: string;
   }[];
   tekster: string[];
   /** When true, skip the expensive current-påtaleberettiget web-search step. */
   skipPaataleberettigetResolution?: boolean | null;
}

export type PaataleberettigetResolutionStatus = "likely" | "ambiguous" | "not_found" | "error";

export interface PaataleberettigetSuccessorStep {
   from: string;
   to: string;
   relationship: string;
   effective_date: string | null;
   evidence: string;
}

export interface PaataleberettigetAlternative {
   name: string;
   reasoning: string;
}

export interface PaataleberettigetSource {
   title: string | null;
   url: string;
}

export interface PaataleberettigetResolution {
   historical_name: string;
   suggested_current_name: string | null;
   status: PaataleberettigetResolutionStatus;
   successor_chain: PaataleberettigetSuccessorStep[];
   reasoning: string;
   confidence?: number;
   alternatives: PaataleberettigetAlternative[];
   sources: PaataleberettigetSource[];
   searched_at: string;
   model: string;
   duration_ms?: number;
   disclaimer: string;
}

export interface LLMAnalysisResultItem {
   id?: string;
   aktId: string | null;
   processing_method: string;
   reasoning: string;
   pages_kept: number[];
   original_pages: number;
   extracted_pages: number;
   summary?: string;
   confidence?: number;
   servitut_type?: "rådighedsservitut" | "tilstandsservitut" | string;
   paataleberettiget?: string;
   paataleberettiget_resolution?: PaataleberettigetResolution;
   case_relevant?: boolean;
   case_relevance_reasoning?: string;
   /** Problemstilling that was sent when this content analysis ran. */
   case_description?: string | null;
   servitut_type_confidence?: number;
   paataleberettiget_confidence?: number;
   case_relevance_confidence?: number;
   overall_confidence?: number;
   overall_confidence_reasoning?: string;
   needs_review?: boolean;
   review_reasons?: string[];
}

export interface LLMAnalysisResult {
   items: LLMAnalysisResultItem[];
   total_processed: number;
   total_requested: number;
   timestamp: string;
}

export interface LLMEvaluateDocumentParams {
   metadata: LLMMetadata;
   pages?: number[];
   pdfBlob?: Blob;
   documentFileName?: string;
   signal?: AbortSignal;
}

export type LLMLocateDocumentParams = Omit<LLMEvaluateDocumentParams, "pages">;

export type LLMAnalyzerErrorCode = "OPENAI_INSUFFICIENT_QUOTA" | "LLM_ANALYSIS_FAILED";

interface LLMAnalyzerErrorPayload {
   detail?: string;
   code?: string;
   upstream_status?: number;
   upstream_code?: string;
}

interface LLMAnalyzerErrorOptions {
   code: LLMAnalyzerErrorCode;
   status: number;
   statusText: string;
   detail?: string;
   upstreamStatus?: number;
   upstreamCode?: string;
   rawBody: string;
}

const LLM_ANALYSIS_TEMPORARILY_UNAVAILABLE_MESSAGE = "AI analysis is temporarily unavailable. Please try again later or contact support.";

export class LLMAnalyzerError extends Error {
   code: LLMAnalyzerErrorCode;
   status: number;
   statusText: string;
   detail?: string;
   upstreamStatus?: number;
   upstreamCode?: string;
   rawBody: string;

   constructor(publicMessage: string, options: LLMAnalyzerErrorOptions) {
      super(publicMessage);
      this.name = "LLMAnalyzerError";
      this.code = options.code;
      this.status = options.status;
      this.statusText = options.statusText;
      this.detail = options.detail;
      this.upstreamStatus = options.upstreamStatus;
      this.upstreamCode = options.upstreamCode;
      this.rawBody = options.rawBody;
   }
}

function parseLLMAnalyzerErrorPayload(errorText: string): LLMAnalyzerErrorPayload | null {
   try {
      const parsed = JSON.parse(errorText) as unknown;
      return parsed && typeof parsed === "object" ? parsed as LLMAnalyzerErrorPayload : null;
   } catch {
      return null;
   }
}

function isOpenAIInsufficientQuota(response: Response, payload: LLMAnalyzerErrorPayload | null, errorText: string): boolean {
   return payload?.code === "OPENAI_INSUFFICIENT_QUOTA"
      || payload?.upstream_code === "insufficient_quota"
      || (response.status === 429 && /insufficient_quota|OPENAI_INSUFFICIENT_QUOTA/i.test(errorText));
}

async function createLLMAnalyzerError(response: Response): Promise<LLMAnalyzerError> {
   const errorText = await response.text().catch(() => "Unknown error");
   const payload = parseLLMAnalyzerErrorPayload(errorText);
   const commonOptions = {
      status: response.status,
      statusText: response.statusText,
      detail: payload?.detail,
      upstreamStatus: payload?.upstream_status,
      upstreamCode: payload?.upstream_code,
      rawBody: errorText
   };

   if (isOpenAIInsufficientQuota(response, payload, errorText)) {
      return new LLMAnalyzerError(
         Localization.getText(LLM_ANALYSIS_TEMPORARILY_UNAVAILABLE_MESSAGE),
         {
            ...commonOptions,
            code: "OPENAI_INSUFFICIENT_QUOTA"
         }
      );
   }

   return new LLMAnalyzerError(
      `${Localization.getText("Failed to analyze PDF")}: ${response.status} ${response.statusText}`,
      {
         ...commonOptions,
         code: "LLM_ANALYSIS_FAILED"
      }
   );
}

function formatDatoloebenrFromAktId(value?: string): string | undefined {
   return value?.replace(/(.{4})(.{2})(.{2})-()/, "$3$2$1-$4")?.trim();
}

export class ANNLLMAnalyzer {
   private pdfServiceUrl: string;
   private pdfServiceApiKey: string;
   private token: string;
   private callerInfo?: LLMAnalyzerCallerInfo;

   constructor(callerInfo?: LLMAnalyzerCallerInfo) {
      this.token = SettingsManager.getSystemSetting("jobs.jobServiceAPIKey");
      this.callerInfo = callerInfo;
      this.pdfServiceUrl = SettingsManager.getSystemSetting("tinglysningApiGatewayHost");
      this.pdfServiceApiKey = SettingsManager.getSystemSetting("tinglysningApiGatewayKey");
   }

   private getHeaders(): Record<string, string> {
      const headers: Record<string, string> = {
         "access-token": this.token
      };

      if (this.callerInfo) {
         headers["user-ref"] = this.callerInfo.userRef;
         headers["user-name"] = this.callerInfo.userName;
         headers["organization-id"] = this.callerInfo.organizationId;
      }

      return headers;
   }

   /** One event per request to the servitut AI backend (dashboard: who / how often). */
   private trackServitutAiRequest(): void {
      void ActionTracker.track("ServitutAI", "Analyze", "Request");
   }

   /** Returns the S3 URL for the akt file (XML). Use when opening link in new tab. */
   async getAktFileUrl(aktId: string, signal?: AbortSignal): Promise<string> {
      if (!this.pdfServiceUrl || !this.pdfServiceApiKey) {
         throw new Error("Missing tinglysning API configuration");
      }
      const apiUrl = `${this.pdfServiceUrl.replace(/\/+$/, "")}/getTinglysningsAkt`;
      const response = await fetch(apiUrl, {
         method: "POST",
         headers: {
            "Content-Type": "application/json",
            "x-api-key": this.pdfServiceApiKey,
         },
         signal,
         body: JSON.stringify({
            AktDokumentId: aktId,
            storeToFile: "true"
         }),
      });

      if (!response.ok) {
         const errorText = await response.text().catch(() => "Unknown error");
         if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
            throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
         }
         throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const raw = await response.json().catch(() => null) as { body?: string; url?: string } | null;
      if (!raw) throw new Error("Invalid API response");

      let inner: { url?: string } | null = null;
      if (typeof raw.body === "string") {
         try {
            inner = JSON.parse(raw.body) as { url?: string };
         } catch {
            inner = null;
         }
      }
      const fileUrl = raw.url ?? inner?.url;
      if (!fileUrl || typeof fileUrl !== "string") {
         throw new Error("API did not return file URL");
      }
      return fileUrl;
   }

   async fetchPdf(aktId: string, signal?: AbortSignal): Promise<Blob> {
      return fetchTingbogPdfBlob(aktId, signal);
   }

   async buildServitutUrlAnalysisMetadata(params: {
      servitut: {
         dokumentFilnavn?: string;
         aktId?: string;
         aktAliasId?: string;
         prioritetNummer?: string | number;
         tekster?: string[];
         tekstSummarisk?: string;
      };
      stamOplysninger?: {
         indskannetAktDokumentId?: string;
         adresse?: {
            streetName?: string;
            buildingIdentifier?: string;
            postCode?: string;
            districtName?: string;
         };
      };
      xmlDoc?: Document | null;
      viamapTingBog?: any;
      propertyData?: any;
      propertyDataList?: any[];
      fetchAktId: string;
      documentUrl?: string;
      id?: string;
   }): Promise<LLMMetadata> {
      const built = this.buildMetadata(
         params.servitut,
         params.stamOplysninger,
         params.xmlDoc,
         params.viamapTingBog,
         params.propertyData,
         { propertyDataList: params.propertyDataList }
      );
      const documentUrl = params.documentUrl ?? await this.getAktFileUrl(params.fetchAktId);
      const searchTargets = getServitutAktSearchTargets(params.servitut, params.stamOplysninger);
      return {
         ...built,
         id: params.id,
         documentUrl,
         fromOvrigDataSource: Boolean(
            searchTargets.ovrigAktId
            && params.fetchAktId === searchTargets.ovrigAktId
            && params.fetchAktId !== searchTargets.korrektAktId
            && params.fetchAktId !== searchTargets.servitutAktId
            && params.fetchAktId !== searchTargets.textAktId,
         ),
      };
   }

   async analyzePdfUrlBatch(metadata: LLMMetadata[], signal?: AbortSignal): Promise<LLMAnalysisResult> {
      this.trackServitutAiRequest();
      const headers = {
         ...this.getHeaders(),
         "Content-Type": "application/json",
      };

      const response = await fetchServitutProcessor("/process-pdf-batch?metadataOnly=true", {
         method: "POST",
         headers,
         signal,
         body: JSON.stringify({ metadata }),
      });

      if (!response.ok) {
         throw await createLLMAnalyzerError(response);
      }

      return await response.json() as LLMAnalysisResult;
   }

   /** Pass 1: find start/end pages in a full akt. Does not extract resume/type/påtale/case relevance. */
   async locateServitutBatch(metadata: LLMMetadata[], signal?: AbortSignal): Promise<LLMAnalysisResult> {
      this.trackServitutAiRequest();
      const headers = {
         ...this.getHeaders(),
         "Content-Type": "application/json",
      };
      const locateMetadata = metadata.map((item) => {
         const { caseDescription: _ignored, ...rest } = item;
         return {
            ...rest,
            skipPaataleberettigetResolution: true,
         };
      });

      const response = await fetchServitutProcessor("/locate-servitut-batch?metadataOnly=true", {
         method: "POST",
         headers,
         signal,
         body: JSON.stringify({ metadata: locateMetadata }),
      });

      if (!response.ok) {
         throw await createLLMAnalyzerError(response);
      }

      return await response.json() as LLMAnalysisResult;
   }

   /** Pass 1 for an uploaded PDF (custom akt / local file). */
   async locateServitutDocument(params: LLMLocateDocumentParams): Promise<LLMAnalysisResultItem> {
      this.trackServitutAiRequest();
      const { caseDescription: _ignored, ...metadataRest } = params.metadata;
      const metadata = {
         ...metadataRest,
         skipPaataleberettigetResolution: true,
      };
      const headers = this.getHeaders();
      let body: BodyInit;

      if (params.pdfBlob) {
         const formData = new FormData();
         const filename = params.documentFileName || params.metadata.document || "servitut.pdf";
         formData.append("metadata", JSON.stringify([{ ...metadata, document: filename }]));
         formData.append("files", new File([params.pdfBlob], filename, { type: params.pdfBlob.type || "application/pdf" }));
         body = formData;
      } else {
         headers["Content-Type"] = "application/json";
         body = JSON.stringify({ metadata: [metadata] });
      }

      const response = await fetchServitutProcessor("/locate-servitut-batch?metadataOnly=true", {
         method: "POST",
         headers,
         signal: params.signal,
         body,
      });

      if (!response.ok) {
         throw await createLLMAnalyzerError(response);
      }

      const payload = await response.json() as LLMAnalysisResult;
      const item = payload.items?.[0];
      if (!item) {
         throw new Error("Locate request returned no items");
      }
      return item;
   }

   async evaluateServitutDocument(params: LLMEvaluateDocumentParams): Promise<LLMAnalysisResultItem> {
      this.trackServitutAiRequest();
      const metadata = {
         ...params.metadata,
         pages: params.pages,
      };
      const headers = this.getHeaders();
      let body: BodyInit;

      if (params.pdfBlob) {
         const formData = new FormData();
         const filename = params.documentFileName || params.metadata.document || "servitut.pdf";
         formData.append("metadata", JSON.stringify(metadata));
         formData.append("file", new File([params.pdfBlob], filename, { type: params.pdfBlob.type || "application/pdf" }));
         body = formData;
      } else {
         headers["Content-Type"] = "application/json";
         body = JSON.stringify(metadata);
      }

      const response = await fetchServitutProcessor("/evaluate-document", {
         method: "POST",
         headers,
         signal: params.signal,
         body,
      });

      if (!response.ok) {
         throw await createLLMAnalyzerError(response);
      }

      const payload = await response.json() as { item?: LLMAnalysisResultItem } | LLMAnalysisResultItem;
      return "item" in payload && payload.item ? payload.item : payload as LLMAnalysisResultItem;
   }

   buildMetadata(
      servitut: {
         dokumentFilnavn?: string;
         aktId?: string;
         aktAliasId?: string;
         prioritetNummer?: string | number;
         tekster?: string[];
         tekstSummarisk?: string;
      },
      stamOplysninger?: {
         indskannetAktDokumentId?: string;
         adresse?: {
            streetName?: string;
            buildingIdentifier?: string;
            postCode?: string;
            districtName?: string;
         };
      },
      xmlDoc?: Document | null,
      _viamapTingBog?: any,
      propertyData?: any,
      options?: { propertyDataList?: any[] }
   ): LLMMetadata {
      const aktIdForMetadata = (servitut?.aktId && servitut.aktId.length > 0)
         ? servitut.aktId
         : (servitut?.aktAliasId && servitut.aktAliasId.length > 0)
            ? servitut.aktAliasId
            : 'unknown';

      const documentFilename = getServitutDownloadFileName({
         datoloebenr: formatDatoloebenrFromAktId(aktIdForMetadata),
         priorityNumber: servitut?.prioritetNummer,
         headingText: servitut?.tekstSummarisk || servitut?.tekster?.join(" ") || "",
         fallbackStem: servitut?.dokumentFilnavn || aktIdForMetadata,
      }).replace(/\.pdf$/i, "");

      const matrikelData = buildLLMMatrikelData({
         propertyData,
         propertyDataList: options?.propertyDataList,
         stamOplysninger,
         xmlDoc,
      });

      const tekster: string[] = [];
      if (servitut?.tekster && Array.isArray(servitut.tekster)) {
         tekster.push(...servitut.tekster);
      }
      if (servitut?.tekstSummarisk) {
         tekster.push(servitut.tekstSummarisk);
      }

      return {
         document: `${documentFilename}.pdf`,
         aktId: aktIdForMetadata,
         matrikelData,
         tekster
      };
   }

}

let analyzerInstance: ANNLLMAnalyzer | null = null;

export function getANNLLMAnalyzer(): ANNLLMAnalyzer {
   if (!analyzerInstance) {
      analyzerInstance = new ANNLLMAnalyzer();
   }
   return analyzerInstance;
}

export function createANNLLMAnalyzer(callerInfo: LLMAnalyzerCallerInfo): ANNLLMAnalyzer {
   return new ANNLLMAnalyzer(callerInfo);
}
